import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { History, Package, MapPin, Truck, User, FileText } from "lucide-react";
import { API } from "@/App";

const ENTITY_TYPES = [
  { value: "all", label: "All Types" },
  { value: "product", label: "Products" },
  { value: "supplier", label: "Suppliers" },
  { value: "location", label: "Locations" },
  { value: "inventory", label: "Inventory" },
  { value: "receiving_session", label: "Receiving Sessions" },
];

const ACTION_COLORS = {
  create: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  update: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  delete: "bg-red-500/10 text-red-500 border-red-500/20",
  transfer: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  adjust: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  complete: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  cancel: "bg-red-500/10 text-red-500 border-red-500/20",
  generate_barcode: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
};

const getEntityIcon = (type) => {
  switch (type) {
    case "product":
      return Package;
    case "supplier":
      return Truck;
    case "location":
      return MapPin;
    case "receiving_session":
      return FileText;
    default:
      return History;
  }
};

export default function AuditLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState("");

  useEffect(() => {
    fetchLogs();
  }, [entityType]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.append("entity_type", entityType);
      const url = `${API}/audit-logs${params.toString() ? `?${params.toString()}` : ""}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  const formatAction = (action) => {
    return action.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatEntityType = (type) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    return Object.entries(details)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
      .join(", ");
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Audit Logs
            </h1>
            <p className="text-muted-foreground">
              Track all changes and activities
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="rounded-sm border">
          <CardContent className="p-4">
            <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="entity-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 skeleton-pulse" />
                ))}
              </div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center">
                <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No audit logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => {
                      const EntityIcon = getEntityIcon(log.entity_type);
                      const actionColor = ACTION_COLORS[log.action] || "bg-muted text-muted-foreground";
                      const details = formatDetails(log.details);

                      return (
                        <TableRow key={index} className="data-row">
                          <TableCell>
                            <Badge className={`rounded-sm border ${actionColor}`}>
                              {formatAction(log.action)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <EntityIcon className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm">{formatEntityType(log.entity_type)}</p>
                                <p className="text-xs text-muted-foreground font-data">
                                  {log.entity_id}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {details ? (
                              <p className="text-sm text-muted-foreground max-w-md truncate">
                                {details}
                              </p>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="font-data text-sm text-muted-foreground whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
